import { useState } from "react";
import { PageHeader, useShell } from "../components/Shell";
import { Card, Chip, EmptyState, ErrorState, LoadingState } from "../components/ui";
import { getInbox, resolveInbox, syncAsc, type InboxItem } from "../lib/api-admin";
import { dateTimeNl, eventTypeMeta, relativeNl } from "../lib/format";
import { getOverviewEventsForInbox } from "./inbox-events";
import { inboxText } from "../lib/inbox-text";
import { useAsync } from "../lib/useAsync";

const TYPE_LABEL: Record<string, string> = {
  url_conflict: "Bestaande notificatie-URL van een andere dienst",
  app_discovered: "Nieuwe app ontdekt",
  product_discovered: "Nieuw product ontdekt",
  asc_conflict: "Conflict bij App Store Connect",
};

export function Inbox() {
  const { settings, refresh } = useShell();
  const inbox = useAsync(() => getInbox(), []);
  const auto = useAsync(() => getOverviewEventsForInbox(), []);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ tone: "ok" | "err"; text: string } | null>(null);

  async function act(item: InboxItem, action: "dismiss" | "adopt") {
    setBusy(item.id);
    setMsg(null);
    try {
      const r = await resolveInbox(item.id, action);
      if (r.skipped) setMsg({ tone: "err", text: `Overgeslagen: ${r.skipped}` });
      inbox.reload();
      refresh();
    } catch (e) {
      setMsg({ tone: "err", text: e instanceof Error ? e.message : "Mislukt" });
    } finally {
      setBusy(null);
    }
  }

  async function sync() {
    setBusy("sync");
    setMsg(null);
    try {
      const r = await syncAsc();
      const skipped = typeof r.skipped === "string" ? r.skipped : null;
      setMsg(skipped ? { tone: "err", text: skipped === "asc_keys_missing" ? "App Store Connect-sleutel ontbreekt; sync overgeslagen." : `Sync overgeslagen: ${skipped}` } : { tone: "ok", text: "Sync gestart en afgerond." });
      inbox.reload();
      refresh();
    } catch (e) {
      setMsg({ tone: "err", text: e instanceof Error ? e.message : "Sync mislukt" });
    } finally {
      setBusy(null);
    }
  }

  const items = inbox.data?.items ?? [];

  return (
    <>
      <PageHeader title="Inbox" subtitle={items.length ? `${items.length} open ${items.length === 1 ? "melding" : "meldingen"}` : "Alles afgehandeld"}>
        <button className="pill pill-primary" onClick={sync} disabled={busy !== null}>
          {busy === "sync" ? "Bezig…" : "Nu syncen"}
        </button>
      </PageHeader>

      {msg && <div className={msg.tone === "ok" ? "banner banner-ok" : "banner banner-warn"} role="status">{msg.text}</div>}

      <Card title="App Store Connect-sync">
        <p className="muted tight">
          {settings?.asc_keys ? `Laatste sync: ${settings.last_asc_sync ? `${relativeNl(settings.last_asc_sync)} (${dateTimeNl(settings.last_asc_sync)})` : "nog niet gedraaid"}. Daarna synct AppHQ automatisch elk uur.` : "De App Store Connect API-sleutel is niet ingesteld, dus er wordt niet gesynct."}
        </p>
      </Card>

      <Card title="Open meldingen">
        {inbox.status === "error" && <ErrorState message={inbox.error} onRetry={inbox.reload} />}
        {!inbox.data && inbox.status === "loading" && <LoadingState />}
        {inbox.data && items.length === 0 && <EmptyState title="Alles afgehandeld" icon="✓">Er zijn geen open meldingen.</EmptyState>}
        {items.length > 0 && (
          <ul className="inbox-list">
            {items.map((it) => {
              const t = inboxText(it.type, it.detail);
              return (
              <li key={it.id}>
                <div className="inbox-main">
                  <Chip tone={it.type === "url_conflict" || it.type === "asc_conflict" ? "warn" : "neutral"}>{TYPE_LABEL[it.type] ?? "Melding"}</Chip>
                  <strong>{t.headline ?? it.title}</strong>
                  {t.summary && <span className="muted">{t.summary}</span>}
                  {t.lines.length > 0 && (
                    <dl className="inbox-facts">
                      {t.lines.map((l) => (
                        <div key={l.label}>
                          <dt className="muted">{l.label}</dt>
                          <dd className={l.mono ? "mono" : undefined}>{l.value}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                  <span className="muted">{dateTimeNl(it.created_at)}</span>
                </div>
                <div className="inbox-actions">
                  {it.type === "url_conflict" ? (
                    <>
                      <button className="pill" disabled={busy !== null} onClick={() => act(it, "dismiss")}>Laten staan</button>
                      <button className="pill pill-primary" disabled={busy !== null} onClick={() => act(it, "adopt")}>Overnemen</button>
                    </>
                  ) : (
                    <button className="pill" disabled={busy !== null} onClick={() => act(it, "dismiss")}>Afhandelen</button>
                  )}
                </div>
              </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Card title="Automatisch gedaan">
        {auto.status === "error" && <ErrorState message={auto.error} onRetry={auto.reload} />}
        {!auto.data && auto.status === "loading" && <LoadingState />}
        {auto.data && auto.data.length === 0 && <EmptyState title="Nog niets automatisch gedaan">Auto-koppelingen en andere systeemacties verschijnen hier.</EmptyState>}
        {auto.data && auto.data.length > 0 && (
          <ul className="events">
            {auto.data.map((e, i) => (
              <li key={`${e.at}-${i}`}>
                <span className={`dot dot-${eventTypeMeta(e.type).tone}`} />
                <span className="events-label">{e.label}</span>
                <span className="muted events-time">{relativeNl(e.at)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
